import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import api from "../../api";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import ReportDeleteModal from "../../components/patients/reports/ReportDeleteModal";
import ReportEditorModal from "../../components/patients/reports/ReportEditorModal";
import ReportsSummaryHeader from "../../components/patients/reports/ReportsSummaryHeader";
import ReportsTableSection from "../../components/patients/reports/ReportsTableSection";

export default function ReportPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [patient, setPatient] = useState();
    const [loading, setLoading] = useState(false);
    const [loadingPrint, setLoadingPrint] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const { idPatient } = useParams();
    const [reports, setReports] = useState([]);
    const [printWithInsurance, setPrintWithInsurance] = useState(true);
    const [userId] = useState(
        JSON.parse(localStorage.getItem("BDOuserData"))?.id
    );
    useEffect(() => {
        api.printTemplates
            .resolve("report", userId)
            .then((r) => setPrintWithInsurance(r.showInsuranceDefault))
            .catch(() => setPrintWithInsurance(true));
    }, [userId]);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [reportForm, setReportForm] = useState({
        patient: idPatient,
        title: "",
        rightEye: "",
        leftEye: "",
        conclusion: "",
    });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    const notify = useCallback((message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
            },
        });
    }, []);

    const pages = [
        { name: "Pacientes", href: "/patients", current: false },
        {
            name: `${patient?.name}`,
            href: `/patients/${idPatient}`,
            current: false,
        },
        { name: "Informes", href: "#", current: true },
    ];

    const loadReports = useCallback(() => {
        setLoading(true);
        return api.reports
            .getMulti("-created_at", null, null, null, { patient: idPatient })
            .then((data) => {
                const items = data?.results ?? data ?? [];
                setReports(items);
            })
            .catch(() => notify("Error al cargar los informes", "error"))
            .finally(() => setLoading(false));
    }, [idPatient, notify]);

    useEffect(() => {
        api.patients
            .getOne(idPatient)
            .then((response) => {
                setPatient({
                    ...response,
                    name: `${response.firstName} ${response.lastName}`,
                });
            })
            .catch((error) => console.log(error));
    }, [idPatient]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    const handleOpenReportModal = (data) => {
        if (data) {
            setReportForm({
                id: data.id,
                patient: data.patient,
                title: data.title || "",
                rightEye: data.rightEye || "",
                leftEye: data.leftEye || "",
                conclusion: data.conclusion || "",
                doctor: data.doctor?.id,
            });
        } else {
            setReportForm({
                patient: idPatient,
                title: "",
                rightEye: "",
                leftEye: "",
                conclusion: "",
            });
        }
        setFormErrors({});
        setIsReportModalOpen(true);
    };

    const handleCloseReportModal = () => {
        setFormErrors({});
        setIsReportModalOpen(false);
    };

    const handleChangeReportForm = (event) => {
        const { name, value } = event.target;
        setReportForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleChangeReportEditor = (name, content) => {
        setReportForm((prev) => ({ ...prev, [name]: content }));
    };

    const handleSubmitReport = async (event) => {
        event.preventDefault();

        const title = (reportForm.title || "").trim();
        const conclusionText = (reportForm.conclusion || "")
            .replace(/<[^>]*>/g, "")
            .trim();

        const errors = {};
        if (!title) errors.title = "El título es obligatorio.";
        if (!conclusionText) errors.conclusion = "La conclusión es obligatoria.";

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});
        setLoading(true);

        try {
            const payload = {
                ...reportForm,
                doctor: reportForm.doctor ?? userId,
            };

            if (reportForm.id) {
                await api.reports.update(reportForm.id, payload);
            } else {
                await api.reports.create(payload);
            }

            notify(reportForm.id ? "Informe actualizado correctamente" : "Informe creado correctamente");
            await loadReports();
            setReportForm({
                patient: idPatient,
                title: "",
                rightEye: "",
                leftEye: "",
                conclusion: "",
            });
            setIsReportModalOpen(false);
        } catch (error) {
            notify("Error al guardar el informe", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteModal = (report) => {
        setReportToDelete(report);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setReportToDelete(null);
        setIsDeleteModalOpen(false);
    };

    const handleConfirmDeleteReport = async () => {
        if (!reportToDelete) return;
        setLoading(true);
        try {
            await api.reports.delete(reportToDelete.id);
            await loadReports();
            setReportToDelete(null);
            setIsDeleteModalOpen(false);
            notify("Informe eliminado correctamente");
        } catch (error) {
            notify("Error al eliminar el informe", "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePrintReport = async (report) => {
        if (!report?.id) return;

        const title = (report.title || "").trim();
        const conclusionText = (report.conclusion || "")
            .replace(/<[^>]*>/g, "")
            .trim();

        if (!title || !conclusionText) {
            notify(
                "No se puede imprimir: el título y la conclusión son obligatorios.",
                "error"
            );
            return;
        }

        setLoadingPrint(true);
        try {
            const data = await api.reportsExport.export(
                report.id,
                printWithInsurance
            );
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${report.title || "informe"}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            notify("Error al generar el PDF", "error");
        } finally {
            setLoadingPrint(false);
        }
    };

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={handleSidebarToggle}
                    mobileBackTo={`/patients/${idPatient}`}
                    mobileBackLabel="Paciente"
                    mobileTitle={patient?.name || "Paciente"}
                />
            }
        >
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-4 lg:px-6 lg:py-6">
                <ReportsSummaryHeader
                    patient={patient}
                    reportsCount={reports.length}
                />

                <ReportsTableSection
                    reports={reports}
                    onOpenReportModal={handleOpenReportModal}
                    loading={loading}
                    onDeleteReport={handleOpenDeleteModal}
                    onPrintReport={handlePrintReport}
                    loadingPrint={loadingPrint}
                    printWithInsurance={printWithInsurance}
                    setPrintWithInsurance={setPrintWithInsurance}
                    isDesktop={isDesktop}
                />

                <ReportEditorModal
                    isOpen={isReportModalOpen}
                    onClose={handleCloseReportModal}
                    onChange={handleChangeReportForm}
                    onEditorChange={handleChangeReportEditor}
                    onSubmit={handleSubmitReport}
                    values={reportForm}
                    loading={loading}
                    errors={formErrors}
                />

                <ReportDeleteModal
                    isOpen={isDeleteModalOpen}
                    report={reportToDelete}
                    loading={loading}
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleConfirmDeleteReport}
                />
            </div>
            <Toaster />
        </PatientModuleLayout>
    );
}
